export type DebugLevel = 'info' | 'warn' | 'error';

export type DebugEvent = {
  t: string;
  level: DebugLevel;
  src: string;
  msg: string;
  data?: unknown;
};

const MAX = 250;
const events: DebugEvent[] = [];
const listeners = new Set<() => void>();

function clip(data: unknown): unknown {
  if (data == null) return data;
  try {
    const s = JSON.stringify(data);
    if (s.length <= 4000) return JSON.parse(s);
    return { truncated: true, preview: s.slice(0, 4000) };
  } catch {
    return String(data).slice(0, 500);
  }
}

export function debugLog(level: DebugLevel, src: string, msg: string, data?: unknown) {
  events.push({ t: new Date().toISOString(), level, src, msg, data: clip(data) });
  if (events.length > MAX) events.shift();
  for (const fn of listeners) fn();
  if (level === 'error') console.error(`[finlab:${src}]`, msg, data ?? '');
  else if (level === 'warn') console.warn(`[finlab:${src}]`, msg, data ?? '');
}

export function debugEvents(): DebugEvent[] {
  return events.slice();
}

export function subscribeDebug(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function formatDebugDump(extra: Record<string, unknown> = {}): string {
  return [
    '# FinLab debug dump',
    `captured_at: ${new Date().toISOString()}`,
    `page: ${typeof location !== 'undefined' ? location.href : ''}`,
    `ua: ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`,
    '',
    '## snapshot',
    '```json',
    JSON.stringify(extra, null, 2),
    '```',
    '',
    '## client events (oldest → newest)',
    '```json',
    JSON.stringify(events, null, 2),
    '```',
    '',
  ].join('\n');
}

export function installGlobalDebugHooks() {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (ev) => {
    debugLog('error', 'window', ev.message || 'window.error', {
      file: ev.filename,
      line: ev.lineno,
      col: ev.colno,
    });
  });
  window.addEventListener('unhandledrejection', (ev) => {
    debugLog('error', 'window', 'unhandledrejection', {
      reason: ev.reason instanceof Error ? ev.reason.message : String(ev.reason),
    });
  });
  (window as unknown as { __FINLAB_DEBUG__: unknown }).__FINLAB_DEBUG__ = {
    events: debugEvents,
    dump: formatDebugDump,
  };
}
